<template>
  <div class="ExperienceFloorOptions flex flex-col gap-12">
    <div class="font-lora text-16 leading-24">{{ $t('pdp.experience.headline') }}</div>

    <div
      class="grid grid-cols-2 gap-12"
      role="radiogroup"
      :aria-label="$t('pdp.experience.headline')"
    >
      <!-- * * * * order sample -->
      <button
        type="button"
        role="radio"
        :aria-checked="selected === 'sample'"
        :disabled="!sampleAvailable"
        class="flex flex-col items-start gap-8 p-12 text-left bg-bg-base"
        :class="selected === 'sample' ? 'border-2 border-border-active' : 'border border-border-subtle'"
        @click="selected = 'sample'"
      >
        <span class="flex items-center justify-between w-full">
          <GlobalIcon name="cart" color="base" />
          <span
            class="w-20 h-20 rounded-full flex items-center justify-center"
            :class="radioClass(selected === 'sample', !sampleAvailable)"
          >
            <span v-if="selected === 'sample'" class="w-8 h-8 rounded-full bg-bg-base" />
          </span>
        </span>

        <span
          v-if="!sampleAvailable"
          class="font-open font-semibold text-10 leading-14 bg-bg-secondary text-text-subtle h-24 px-8 flex items-center"
        >
          {{ $t('stock.tempSoldoutShort') }}
        </span>

        <span class="flex flex-col gap-2 w-full" :class="sampleAvailable ? 'text-text-base' : 'text-text-inactive'">
          <span class="font-open font-semibold text-14 leading-20">{{ $t('samples.b2b.orderSample') }}</span>
          <span class="font-open text-12 leading-16" :class="{ 'text-text-subtle': sampleAvailable }">{{ $t('pdp.experience.sample.text') }}</span>
        </span>

        <span class="flex items-center gap-8 font-open text-10 leading-14" :class="sampleAvailable ? 'text-text-subtle' : 'text-text-inactive opacity-50'">
          <span
            v-for="src in swatchSources"
            :key="src"
            class="w-16 h-16 border border-border-base overflow-hidden shrink-0"
          >
            <nuxt-img
              :src="src"
              provider="norce"
              preset="plpimage"
              fit="contain"
              width="16"
              height="16"
              alt=""
              class="w-full h-full object-cover"
              @error="onSwatchError(src)"
            />
          </span>
          {{ $t('pdp.experience.sample.format') }}
        </span>
      </button>

      <!-- * * * * see floor in store -->
      <button
        type="button"
        role="radio"
        :aria-checked="selected === 'store'"
        class="flex flex-col items-start gap-8 p-12 text-left bg-bg-base"
        :class="selected === 'store' ? 'border-2 border-border-active' : 'border border-border-subtle'"
        @click="selected = 'store'"
      >
        <span class="flex items-center justify-between w-full">
          <GlobalIcon name="shop" color="base" />
          <span
            class="w-20 h-20 rounded-full flex items-center justify-center"
            :class="radioClass(selected === 'store', false)"
          >
            <span v-if="selected === 'store'" class="w-8 h-8 rounded-full bg-bg-base" />
          </span>
        </span>

        <span class="flex flex-col gap-2 w-full grow text-text-base">
          <span class="font-open font-semibold text-14 leading-20">{{ $t('pdp.experience.store.title') }}</span>
          <span class="font-open text-12 leading-16 text-text-subtle">{{ $t('pdp.experience.store.text') }}</span>
        </span>

        <span class="flex items-center gap-8 font-open text-10 leading-14 text-text-subtle">
          <span
            v-for="src in swatchSources"
            :key="src"
            class="w-32 h-16 border border-border-base overflow-hidden shrink-0"
          >
            <nuxt-img
              :src="src"
              provider="norce"
              preset="plpimage"
              fit="contain"
              width="32"
              height="16"
              alt=""
              class="w-full h-full object-cover"
              @error="onSwatchError(src)"
            />
          </span>
          {{ $t('pdp.experience.store.format') }}
        </span>
      </button>
    </div>

    <!-- * * * * sample panel -->
    <div v-show="selected === 'sample'" class="bg-bg-secondary p-12 desk:p-16 flex flex-col gap-12">
      <div class="flex gap-12 items-start">
        <span
          v-for="src in swatchSources"
          :key="src"
          class="w-56 h-56 shrink-0 overflow-hidden"
        >
          <nuxt-img
            :src="src"
            provider="norce"
            preset="plpimage"
            fit="contain"
            width="56"
            height="56"
            alt=""
            class="w-full h-full object-cover"
            @error="onSwatchError(src)"
          />
        </span>
        <div class="flex flex-col gap-2">
          <div class="font-lora text-16 desk:text-18 leading-24 desk:leading-28">{{ $t('pdp.experience.samplePanel.title') }}</div>
          <div class="font-open text-12 desk:text-14 leading-16 desk:leading-20 text-text-subtle">{{ $t('pdp.experience.samplePanel.text') }}</div>
        </div>
      </div>
      <BuyBoxSamplesOnly
        ref="sampleBox"
        :active-variant="activeVariant"
        :product-item="productItem"
        new-design
        class="[&>div]:mt-0"
        @loaded="onSampleLoaded"
      />
    </div>

    <!-- * * * * store panel -->
    <div v-show="selected === 'store'" class="bg-bg-secondary p-12 desk:p-16 flex flex-col gap-12">
      <div class="flex flex-col gap-2">
        <div class="font-lora text-16 desk:text-18 leading-24 desk:leading-28">{{ $t('pdp.experience.storePanel.title') }}</div>
        <div class="font-open text-12 desk:text-14 leading-16 desk:leading-20 text-text-subtle">{{ $t('pdp.experience.storePanel.text') }}</div>
      </div>

      <template v-if="!locationStore.location">
        <div class="flex flex-col gap-8">
          <label for="experienceStoreSearch" class="font-open font-semibold text-12 leading-16">{{ $t('pdp.experience.storePanel.label') }}</label>
          <div class="relative">
            <input
              id="experienceStoreSearch"
              v-model="searchTerm"
              type="text"
              :placeholder="$t('pdp.experience.storePanel.placeholder')"
              class="w-full h-48 px-12 py-8 bg-bg-base border border-border-base font-open text-12 leading-16"
              @keydown.enter="searchByTerm"
            >
            <button
              type="button"
              class="btn !absolute right-4 top-4 px-12 font-open font-semibold text-12 leading-16"
              :class="{ loading: searching }"
              @click="searchByTerm"
            >
              {{ $t('floorPlannerSelector.search') }}
            </button>
          </div>
          <div v-if="errorKey" class="font-open text-12 leading-16 text-text-error">{{ $t(errorKey) }}</div>
        </div>

        <button
          type="button"
          class="flex items-center gap-8 w-fit font-open text-12 leading-16 underline"
          :disabled="locating"
          @click="useMyPosition"
        >
          <GlobalIcon
            name="cursor-target"
            size="xsmall"
            color="base"
            class="!w-16 !h-16"
          />
          {{ locating ? $t('pdp.experience.storePanel.locating') : $t('pdp.experience.storePanel.useLocation') }}
        </button>
      </template>

      <template v-else>
        <div class="bg-bg-base border border-border-base py-4 pl-16 pr-8 flex items-center gap-12">
          <GlobalIcon
            name="location"
            size="small"
            color="base"
          />
          <div class="grow flex flex-col py-8" role="status">
            <span class="font-open text-12 leading-16 text-text-subtle">{{ $t('pdp.experience.storePanel.showingNear') }}</span>
            <span class="font-open font-semibold text-14 leading-24" :class="{ capitalize: locationStore.location?.label }">{{ locationLabel }}</span>
          </div>
          <button
            type="button"
            class="font-open font-semibold text-14 leading-20 underline px-8 min-h-44"
            :aria-label="$t('pdp.experience.storePanel.changeAria', { location: locationLabel })"
            @click="changeLocation"
          >
            {{ $t('mypage.orderProposal.edit') }}
          </button>
        </div>

        <ExperienceStoreResults :part-no="activeVariant.partNo" />

        <div v-if="sampleAvailable" class="pt-12 border-t border-border-base">
          <button
            type="button"
            class="w-full flex items-center justify-between gap-12 py-4 text-left"
            :disabled="sampleBox?.loading"
            @click="sampleBox?.addSampleToCart()"
          >
            <span class="flex flex-col">
              <span class="font-open text-12 leading-16 text-text-subtle">{{ $t('pdp.experience.storePanel.tryAtHome') }}</span>
              <span class="font-open font-semibold text-14 leading-20 underline">
                {{ sampleBox?.inCart ? $t('quickbuy.button.inCart') : $t('pdp.experience.storePanel.orderSampleLink', { price: useLocalizedCurrency(samplePrice, true) }) }}
              </span>
            </span>
            <img
              v-if="sampleBox?.loading"
              src="/icons/spinner.svg"
              class="shrink-0 w-20 h-20 spin"
              alt=""
            >
            <GlobalIcon
              v-else
              name="arrow-right"
              size="small"
              color="base"
              class="shrink-0 !w-20 !h-20"
            />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import BuyBoxSamplesOnly from '~/components/product-page/BuyBoxSamplesOnly.vue';
import ExperienceStoreResults from '~/components/product-page/ExperienceStoreResults.vue';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import useLocationSearch from '~/composables/useLocationSearch';
import { useLocationStore } from '~/store/location';

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
}>();

const locationStore = useLocationStore();

const selected = ref<'sample' | 'store'>('sample');
const sampleBox = useTemplateRef('sampleBox');
const sampleAvailable = ref(true);

const swatchCandidates = computed(() => [
  props.productItem.mainProductImage[0]?.file,
  props.activeVariant.image,
  ...props.productItem.imagesAndVideo(undefined, props.activeVariant, ['SwatchImage']).map(image => image.file),
].filter((src, i, all): src is string => !!src && all.indexOf(src) === i));

// a file that exists in Norce can still fail to load, then the next image is tried
const failedSwatches = ref<string[]>([]);
const swatchSources = computed(() => swatchCandidates.value.filter(src => !failedSwatches.value.includes(src)).slice(0, 1));
const onSwatchError = (src: string) => {
  if (!failedSwatches.value.includes(src)) {
    failedSwatches.value.push(src);
  }
};

const radioClass = (checked: boolean, disabled: boolean) => {
  if (disabled) {
    return 'bg-bg-inactive border border-border-inactive opacity-50';
  }
  return checked ? 'bg-bg-active' : 'bg-bg-base border border-border-base';
};

const samplePrice = ref(0);

const onSampleLoaded = (available: boolean, price: number) => {
  sampleAvailable.value = available;
  samplePrice.value = price;
  if (!available) {
    selected.value = 'store';
  }
};

const { searchTerm, searching, locating, errorKey, searchByTerm, useMyPosition } = useLocationSearch();

onMounted(() => locationStore.load());

const { $t } = useNuxtApp();
const locationLabel = computed(() => locationStore.location?.label || $t('pdp.experience.storePanel.currentPosition'));

const changeLocation = () => {
  searchTerm.value = locationStore.location?.label ?? '';
  locationStore.clear();
};
</script>
